/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminLayout.tsx (v4 — +System +Admins)
 * ============================================================
 */

import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { Shield, Users, Home, BarChart3, LogOut, Loader2, Cpu, UserCog, Mail } from 'lucide-react';
import { useAdminSession, useAdminLogout } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

export default function AdminLayout() {
  const { data: admin, isLoading } = useAdminSession();
  const logoutMut = useAdminLogout();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logoutMut.mutate(undefined, { onSuccess: () => navigate('/admin/login', { replace: true }) });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/kejja-white.svg" alt="Kejja" className="h-5 w-auto" />
            <div className="h-4 w-px bg-slate-700"></div>
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3 text-rose-400" />
              <span className="text-[10px] font-bold text-rose-400 uppercase tracking-widest">Admin</span>
            </div>
          </div>

          <nav className="flex items-center gap-1 ml-4 overflow-x-auto">
            <AdminLink to="/admin"        end icon={BarChart3} label="Overview" />
            <AdminLink to="/admin/users"      icon={Users}     label="Users" />
            <AdminLink to="/admin/homes"      icon={Home}      label="Homes" />
            <AdminLink to="/admin/admins"     icon={UserCog}   label="Admins" />
            <AdminLink to="/admin/emails"     icon={Mail}      label="Emails" />
            <AdminLink to="/admin/system"     icon={Cpu}       label="System" />
          </nav>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <div className="text-xs text-slate-200 font-medium leading-tight">{admin.displayName}</div>
              <div className="text-[10px] text-slate-500 leading-tight">{admin.email}</div>
            </div>
            <div className="h-7 w-7 rounded-full bg-rose-600/15 border border-rose-600/30 text-rose-400 flex items-center justify-center text-[10px] font-bold">
              {(admin.displayName || admin.email).slice(0, 2).toUpperCase()}
            </div>
            <button
              onClick={handleLogout}
              disabled={logoutMut.isPending}
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function AdminLink({ to, end, icon: Icon, label }: {
  to: string; end?: boolean; icon: React.ComponentType<{ className?: string }>; label: string;
}) {
  return (
    <NavLink to={to} end={end}
      className={({ isActive }) => cn(
        'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap',
        isActive ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800/50',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </NavLink>
  );
}
